import { useEffect, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, TextField, Typography, Box,
} from '@mui/material';
import { updateClient } from '../../services/clientService';
import type { Client } from '../../types/project';

interface EditClientDialogProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditClientDialog = ({ open, client, onClose, onUpdated }: EditClientDialogProps) => {
  const [clientName, setClientName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (client) {
      setClientName(client.ClientName || '');
      setContactPerson(client.ContactPerson || '');
      setEmail(client.Email || '');
      setPhone(client.Phone || '');
      setError('');
    }
  }, [client]);

  const handleSubmit = async () => {
    if (!client) return;
    if (!clientName.trim()) { setError('Client Name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await updateClient(client.ClientKey, {
        ClientName: clientName.trim(),
        ContactPerson: contactPerson.trim() || undefined,
        Email: email.trim() || undefined,
        Phone: phone.trim() || undefined,
      });
      onUpdated();
    } catch {
      setError('Failed to update client.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Edit Client</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Client Name" required value={clientName}
          onChange={(e) => setClientName(e.target.value)} autoFocus />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" label="Contact Person" value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)} />
          <TextField size="small" label="Phone" value={phone}
            onChange={(e) => setPhone(e.target.value)} />
        </Box>
        <TextField size="small" label="Email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} />
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
